import { useState } from "react";
import { ChartBar, UsersThree, CaretDown, ArrowRight, Heart } from "@phosphor-icons/react";

const BADGES = [
  { id: "first_game", name: "First Game", icon: "🎮" },
  { id: "word_master", name: "Word Master", icon: "📚" },
  { id: "speed_demon", name: "Speed Star", icon: "⚡" },
  { id: "perfect_score", name: "Perfect!", icon: "🌟" },
  { id: "story_teller", name: "Storyteller", icon: "✨" },
  { id: "creative_genius", name: "Creative", icon: "🎨" },
  { id: "sentence_smith", name: "Sentence Smith", icon: "🧚‍♀️" },
];
const PANEL = "bg-white rounded-[1.5rem] border border-[#e6ddfa] shadow-[0_8px_30px_rgba(115,88,179,0.08)]";

// Private learning journey (existing profile-entry flow) + parents accordion.
export const KidsJourneyRow = ({ player, onContinueProfile }) => {
  const [journeyOpen, setJourneyOpen] = useState(false);
  const [parentsOpen, setParentsOpen] = useState(false);
  const earned = BADGES.filter((b) => player?.badges?.includes(b.id)).length;
  return (
    <div className="grid md:grid-cols-2 gap-5 mt-6">
      <section className={`${PANEL} p-5 sm:p-6`} aria-labelledby="journey-title" data-testid="kids-my-progress">
        <div className="flex items-center gap-4">
          <span className="w-12 h-12 rounded-2xl bg-[#efe7fb] flex items-center justify-center shrink-0"><ChartBar size={26} weight="duotone" className="text-[#6a3fc7]" /></span>
          <div className="min-w-0 flex-1">
            <h2 id="journey-title" className="font-display font-bold text-xl sm:text-2xl text-[#3d2378]">{player ? `${player.name}’s learning journey` : "Your learning journey"}</h2>
            <p className="font-nunito text-sm text-[#5b5572]">{player ? `${player.total_points || 0} points · ${player.games_played || 0} games · ${earned} of ${BADGES.length} badges` : "Your progress stays private."}</p>
          </div>
          {player ? (
            <button type="button" onClick={() => setJourneyOpen((o) => !o)} aria-expanded={journeyOpen} className="inline-flex items-center gap-1.5 font-nunito font-bold text-sm text-[#6a3fc7] underline underline-offset-4 whitespace-nowrap focus-visible:ring-4 focus-visible:ring-[#b79cf0] rounded-full px-1" data-testid="kids-journey-toggle">
              {journeyOpen ? "Hide badges" : "See badges"} <CaretDown size={16} weight="bold" className={`transition-transform ${journeyOpen ? "rotate-180" : ""}`} />
            </button>
          ) : (
            <button type="button" onClick={onContinueProfile} className="inline-flex items-center gap-1.5 font-nunito font-bold text-sm text-[#6a3fc7] underline underline-offset-4 whitespace-nowrap focus-visible:ring-4 focus-visible:ring-[#b79cf0] rounded-full px-1" data-testid="kids-continue-profile-btn">
              Continue your profile <ArrowRight size={16} weight="bold" />
            </button>
          )}
        </div>
        {player && journeyOpen && (
          <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 mt-5" data-testid="kids-badges">
            {BADGES.map((b) => {
              const has = player?.badges?.includes(b.id);
              return (
                <div key={b.id} className={`aspect-square rounded-xl flex flex-col items-center justify-center p-1.5 ${has ? "bg-[#fff6e0] border border-[#f7dca0]" : "bg-[#f7f4fd] border border-dashed border-[#d9ccf5]"}`} data-testid={`badge-${b.id}`}>
                  <span className={`text-xl ${!has && "grayscale opacity-30"}`}>{b.icon}</span>
                  <span className="font-nunito text-[10px] text-center leading-tight mt-0.5 text-[#4a2e8a]">{b.name}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className={PANEL} data-testid="parents-panel">
        <button type="button" onClick={() => setParentsOpen((o) => !o)} aria-expanded={parentsOpen} aria-controls="kids-parents-body" className="w-full flex items-center gap-4 p-5 sm:p-6 text-left rounded-[1.5rem] focus-visible:ring-4 focus-visible:ring-[#b79cf0]" data-testid="parents-panel-toggle">
          <span className="w-12 h-12 rounded-2xl bg-[#efe7fb] flex items-center justify-center shrink-0"><UsersThree size={26} weight="duotone" className="text-[#6a3fc7]" /></span>
          <span className="min-w-0 flex-1">
            <span className="block font-display font-bold text-xl sm:text-2xl text-[#3d2378]">For parents and caregivers</span>
            <span className="block font-nunito text-sm text-[#5b5572]">Tips, discussion ideas, and how to support language and emotional growth at home.</span>
          </span>
          <CaretDown size={22} weight="bold" className={`text-[#6a3fc7] shrink-0 transition-transform ${parentsOpen ? "rotate-180" : ""}`} />
        </button>
        {parentsOpen && (
          <div id="kids-parents-body" className="px-5 sm:px-6 pb-6 font-nunito text-base text-[#5b5572] leading-relaxed space-y-3" data-testid="parents-panel-body">
            <p>Sit beside your child for the first few minutes, then let them lead. Lumi never rushes, scores publicly, or shows ads—progress stays private on this device.</p>
            <p>After an activity, ask: “Which word was new today?” and use it together once more before bedtime.</p>
            <a href="/resources" className="inline-flex items-center gap-2 font-bold text-[#6a3fc7] underline underline-offset-4" data-testid="grownups-resources-link">Parent &amp; Teacher Resources <ArrowRight size={16} weight="bold" /></a>
          </div>
        )}
      </section>
    </div>
  );
};

export const KidsFooter = () => (
  <footer className="mt-10 pt-6 border-t border-[#eadfce] flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="kids-footer">
    <a href="/" className="font-display font-extrabold text-[#3d2378] leading-tight text-center sm:text-left"><span aria-hidden="true" className="text-[#f2b544] text-sm mr-1">✦</span>Embrace the<br className="hidden sm:block" /> <span className="text-[#6a3fc7]">Magic of Words</span></a>
    <nav className="flex flex-wrap justify-center gap-x-5 gap-y-2 font-nunito text-sm text-[#5b5572]" aria-label="Footer">
      <a href="/" className="hover:text-[#4a2e8a]">Home</a>
      <a href="/kids-corner" className="hover:text-[#4a2e8a]">Kids Corner</a>
      <a href="/resources" className="hover:text-[#4a2e8a]">Resources</a>
    </nav>
    <p className="inline-flex items-center gap-2 font-nunito text-xs text-[#5b5572] text-center"><Heart size={16} weight="fill" className="text-[#e75480]" /> Brighter Conversations · A Kinder Tomorrow</p>
  </footer>
);
